// Komponens felelőssége: A keresési mező és gomb megjelenítése, melyekkel a felhasználó keresést indíthat.

import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, TextField, Typography, Paper } from '@mui/material';

import { fetchPaginatedExercises } from '../utils/fetchData'; // Külső funkciók, melyek a szükséges adatokat kérik le
import { Exercise } from '../utils/types'; // Az Exercise típus importálása

// A komponens bemeneti propjainak típusa, melyek a setExercises és setBodyPart függvényeket tartalmazzák.
interface SearchExercisesProps {
  setExercises: React.Dispatch<React.SetStateAction<Exercise[]>>; // Állapot módosító függvény a keresett gyakorlatok listájának beállítására
  bodyPart: string;
  setBodyPart: React.Dispatch<React.SetStateAction<string>>; // Állapot módosító függvény a testrész beállítására
}

// A SearchExercises komponens a keresési mezőt és gombot jeleníti meg, melyekkel a felhasználó keresést indíthat.
const SearchExercises: React.FC<SearchExercisesProps> = ({ setExercises, bodyPart, setBodyPart }) => {
  // A keresési mező állapotának kezelése
  const [search, setSearch] = useState<string>(''); // A keresési mező szövege
  const [bodyParts, setBodyParts] = useState<string[]>([]);  // A rendelkezésre álló testrészek listája
  const [allExercises, setAllExercises] = useState<Exercise[]>([]); // Az összes gyakorlat tárolása

  // Az összes gyakorlat és testrész lekérése a komponens betöltésekor
  useEffect(() => {
    const fetchExercisesData = async () => {
      try {
        // Fetch body parts
        const bodyPartsData: string[] = await fetchPaginatedExercises(
          'https://exercisedb.p.rapidapi.com/exercises/bodyPartList',
          10, 0
        );
        setBodyParts(['all', ...bodyPartsData]); // Add 'all' category at the beginning

        // Fetch all exercises for filtering
        const exercisesData: Exercise[] = await fetchPaginatedExercises(
          'https://exercisedb.p.rapidapi.com/exercises',
          0
        );
        setAllExercises(exercisesData); // Store all exercises
      } catch (error) {
        console.error('Error fetching exercises data:', error);
      }
    };

    fetchExercisesData(); // Fetch the exercises and body parts data
  }, []); // Run only once on component mount


  // A keresés funkciója, mely a keresett szöveg alapján szűri az összes gyakorlatot
  const handleSearch = async () => {
    // Ha a keresés mező nem üres
    if (search) {
      const searchedExercises = allExercises.filter((item: Exercise) =>
        item.name.toLowerCase().includes(search) || // A gyakorlat nevének keresése
        item.target.toLowerCase().includes(search) || // A cél-izom keresése
        item.equipment.toLowerCase().includes(search) || // Az eszköz alapján keresés
        item.bodyPart.toLowerCase().includes(search) // A testrész alapján keresés
      );

      // smooth görgetés a keresés után, a listázott eredményhez
      window.scrollTo({ top: 1800, left: 100, behavior: 'smooth' });

      setSearch(''); // A keresési mező törlése
      setExercises(searchedExercises); // A szűrt gyakorlatok beállítása
    }
  };

  return (
    <Stack alignItems="center" mt="37px" justifyContent="center" p="20px">
      {/* A keresési szekció fejlécének megjelenítése, A Paper egy izléses material UI elem */}
      <Paper
        elevation={4}
        sx={{
          p: 2,
          backgroundColor: '#f0f4f8',
          borderRadius: 2,
          boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)',
          mb: 2,
          textAlign: 'center',
          width: '100%',
          maxWidth: 600,
        }}
      >
        <Typography
          sx={{
            fontWeight: 'bold', color: 'black', fontSize: '26px',
          }}
        >
          Search for your favorite exercises
        </Typography>
      </Paper>
      {/* A keresési mező és gomb */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'left',
        alignItems: 'center',
        mt: '10px',
        mb: '50px',
        '@media (max-width: 600px)': {
          marginLeft: '0px',
        },
      }}>
        <Stack direction="row" spacing={2}>
          <TextField
            id="outlined-basic"
            label="Search"
            variant="outlined"
            value={search}
            onChange={(e) => setSearch(e.target.value.toLowerCase())}
            sx={{
              input: { borderRadius: '10px' },
              width: '400px',
              '@media (max-width: 600px)': {
                width: '350px',
              },
              '@media (max-width: 480px)': {
                width: '300px',
              },
            }}
          />
          <Button
            variant="contained"
            className="search-btn"
            onClick={handleSearch}
            sx={{
              borderRadius: '10px',
              width: { sm: '100px', xs: '80px' },
              position: 'relative',
            }}
          >
            Search
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};

export default SearchExercises;
